import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Radio, ArrowRight, Coins, Gift, Dices, PawPrint, Users, MessageCircle, Crown } from "lucide-react";
import { api } from "@/lib/api";
import { timeAgo } from "@/lib/timeAgo";
import { LIVE } from "@/components/landing/GamesShowcase";
import { H2 } from "@/components/landing/Kit";

const CAT = {
  points: [Coins, "from-amber-300 to-orange-500"], gift: [Gift, "from-rose-400 to-pink-600"], game: [Dices, "from-violet-500 to-pink-500"],
  pet: [PawPrint, "from-emerald-400 to-teal-500"], community: [Users, "from-sky-400 to-indigo-500"], post: [MessageCircle, "from-fuchsia-500 to-rose-500"],
  supporter: [Crown, "from-amber-300 to-yellow-600"],
};
const SHOW = 6;
const DEMO = LIVE.map(([, text], i) => ({ id: `demo${i}`, cat: ["game", "game", "game", "gift"][i], text, created_at: null, demo: true }));

function useLiveFeed() {
  const [shown, setShown] = useState([]);
  const pool = useRef([]);
  const seen = useRef(new Set());
  const cursor = useRef(0);
  const seq = useRef(0);
  useEffect(() => {
    let first = true;
    const load = () => api.get("/public/live-feed").then(({ data }) => {
      const list = data?.length ? data : DEMO;
      pool.current = list;
      if (first) {
        first = false;
        list.forEach((m) => seen.current.add(m.id));
        setShown(list.slice(0, SHOW).map((m) => ({ ...m, k: `${m.id}-${seq.current++}` })));
        cursor.current = Math.min(SHOW, list.length) % list.length;
      }
    }).catch(() => { if (first) { first = false; pool.current = DEMO; setShown(DEMO.map((m) => ({ ...m, k: m.id }))); } });
    load();
    const poll = setInterval(() => { if (!document.hidden) load(); }, 3000);
    const tick = setInterval(() => {
      const list = pool.current;
      if (document.hidden || list.length <= 1) return;
      let next = list.find((m) => !seen.current.has(m.id));
      if (next) seen.current.add(next.id);
      else { next = list[cursor.current % list.length]; cursor.current += 1; }
      setShown((s) => [{ ...next, k: `${next.id}-${seq.current++}`}, ...s].slice(0, SHOW));
    }, 2200);
    return () => { clearInterval(poll); clearInterval(tick); };
  }, []);
  return shown;
}

export function LiveCommunity() {
  const items = useLiveFeed();
  const real = items.length > 0 && !items[0]?.demo;
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14" data-testid="landing-live-section">
      <div className="grid lg:grid-cols-[2fr_3fr] gap-8 lg:gap-12 items-start">
        <div className="space-y-4 fade-up lg:sticky lg:top-24">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-extrabold uppercase tracking-wider" data-testid="landing-live-badge">
            <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-white animate-ping" /><span className="relative w-2 h-2 rounded-full bg-white" /></span> Live
          </span>
          <h2 className={`${H2} `}>Das passiert gerade <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-fuchsia-500">live in der Community</span></h2>
          <p className="text-slate-600">Punkte, Spiele, Geschenke, neue Freundschaften und Beiträge – alles, was gerade in der Community passiert, im Sekundentakt.</p>
          <Link to="/registrieren" data-testid="landing-live-cta" className="inline-flex items-center gap-2 h-12 px-6 rounded-full pink-gradient text-white font-extrabold text-sm shadow-lg shadow-rose-200 hover:scale-[1.03] transition-transform">Jetzt mitmachen <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="rounded-[2rem] bg-white/80 backdrop-blur border border-pink-100 p-4 sm:p-6 shadow-xl shadow-rose-100/60" data-testid={real ? "landing-live-list-real" : "landing-live-list-demo"}>
          <div className="flex items-center justify-between mb-3 px-1">
            <p className="text-xs font-extrabold uppercase tracking-wider text-rose-500 flex items-center gap-1.5"><Radio className="w-4 h-4" /> Neueste Aktionen</p>
            {!real && items.length > 0 && <span className="text-[11px] text-slate-400">Beispiele</span>}
          </div>
          <ul className="space-y-2.5 min-h-[420px] overflow-hidden">
            <AnimatePresence initial={false}>
              {items.map((m, i) => {
                const [Icon, g] = CAT[m.cat] || CAT.community;
                return (
                  <motion.li key={m.k} layout initial={{ opacity: 0, y: -24, scale: 0.95 }} animate={{ opacity: 1 - i * 0.1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }} transition={{ type: "spring", stiffness: 300, damping: 26 }}
                    className={`flex items-center gap-3 rounded-2xl bg-white border border-pink-100 px-4 py-3 ${i === 0 ? "shadow-md shadow-rose-100" : ""}`} data-testid={`landing-live-item-${i}`}>
                    <span className={`w-10 h-10 shrink-0 rounded-full bg-gradient-to-br ${g} flex items-center justify-center`}><Icon className="w-5 h-5 text-white" /></span>
                    <span className="min-w-0 flex-1 text-sm font-semibold text-slate-700">{m.text}</span>
                    {m.created_at && <span className="text-[11px] font-medium text-slate-400 shrink-0">{timeAgo(m.created_at)}</span>}
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        </div>
      </div>
    </section>
  );
}
